import React from 'react';
import { CDBTable, CDBTableBody, CDBTableHeader, CDBIcon } from 'cdbreact';
import { Button } from 'react-bootstrap';
import moment from 'moment-timezone';

const TransactionTable = ({
    transactions,
    allColumns,
    selectedColumns,
    userRole,
    isAccount,
    getCardImage,
    getEstadoEtiqueta,
    renderEstado,
    handleShowModal
}) => {
    const estadoFormato = (estado) => estado === "DEBIT" ? "Debito" : "Credito";

    const calculateTotals = () => {
        const sales = transactions.reduce((total, tx) => {
            if (tx.transaction_status === "aprobada" && tx.transaction_type !== "devolucion") {
                const amount = tx.total !== undefined && tx.total !== null ? tx.total : tx.total_amount;
                return total + (parseFloat(amount) || 0);
            }
            return total;
        }, 0);

        const returns = transactions.reduce((total, tx) => {
            if (tx.transaction_status === "aprobada" && tx.transaction_type === "devolucion") {
                const amount = tx.total !== undefined && tx.total !== null ? tx.total : tx.total_amount;
                return total + (parseFloat(amount) || 0);
            }
            return total;
        }, 0);

        return { sales, returns, net: sales - returns };
    };

    const totals = calculateTotals();

    return (
        <CDBTable
            hover
            responsive
            maxHeight="70vh"
            scrollY
            className="responsive-table"
            style={{ fontSize: '15px', fontFamily: 'Montserrat, sans-serif' }}
        >
            <CDBTableHeader>
                <tr style={{ textAlign: "center", backgroundColor: "#3E4144", color: "white" }}>
                    {allColumns.map(column => (
                        (column.alwaysShow || selectedColumns.includes(column.key)) && (
                            <th key={column.key} style={{ borderTop: '1px solid #DCDCDC', borderBottom: '1px solid #DCDCDC', backgroundColor: '#DCDCDC', color: '#3E4144' }}>
                                {column.label}
                            </th>
                        )
                    ))}
                </tr>
            </CDBTableHeader>
            <br />
            <CDBTableBody>
                {transactions.map((transaccion) => {
                    const adjustedCommission = (parseFloat(transaccion.commission) || 0) + (parseFloat(transaccion.vat) || 0);
                    
                    return (
                        <tr key={transaccion.transaction_id}>
                            {allColumns.map((column) => {
                                if (!(column.alwaysShow || selectedColumns.includes(column.key))) return null;

                                switch (column.key) {
                                    case "ID": return <td key="ID" data-label="ID">{transaccion.transaction_id}</td>;
                                    case "Fecha": return <td key="Fecha" data-label="Fecha">{moment(transaccion.date).format("DD/MM/YYYY")}</td>;
                                    case "Hora": return <td key="Hora" data-label="Hora" title={`Hora por tu Zona Horaria: ${moment.tz.guess()}`}>{moment(transaccion.date).format("HH:mm")}</td>;
                                    case "Terminal": return <td key="Terminal" data-label="Terminal">{transaccion.device}</td>;
                                    case "Detalles": return <td key="Detalles" data-label="Detalles">{transaccion.details}</td>;
                                    case "No.Tarjeta":
                                        const imageSrc = getCardImage(transaccion.card_brand);
                                        return (
                                            <td key="No.Tarjeta" data-label="No.Tarjeta">
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    {imageSrc && <img src={imageSrc} alt={`${transaccion.card_brand} logo`} style={{ width: "40px", height: "20px", marginRight: "5px" }} />}
                                                    <span>{isAccount ? `******${transaccion.masked_card.slice(-4)}` : transaccion.masked_card.slice(-8)}</span>
                                                </div>
                                            </td>
                                        );
                                    case "Tarjeta": return <td key="Tarjeta" data-label="Tarjeta">{transaccion.card_brand}</td>;
                                    case "Tipo": return <td key="Tipo" data-label="Tipo">{estadoFormato(transaccion.card_type)}</td>;
                                    case "Tipo Tx": return <td key="Tipo Tx" data-label="Tipo Tx">{transaccion.transaction_type}</td>;
                                    case "Subtotal":
                                        const subtotal = transaccion.total !== undefined && transaccion.total !== null ? transaccion.total_amount : transaccion.subtotal;
                                        return <td key="Subtotal" data-label="Subtotal">${parseFloat(subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>;
                                    case "Propina": return <td key="Propina" data-label="Propina">${parseFloat(transaccion.tip || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>;
                                    case "Transacción":
                                        const totalAmount = transaccion.total !== undefined && transaccion.total !== null ? transaccion.total : transaccion.total_amount;
                                        return <td key="Transacción" data-label="Transacción">${parseFloat(totalAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>;
                                    case 'Sucursal': return <td key="Sucursal" data-label="Sucursal">{transaccion.branch}</td>;
                                    case "MSI": return <td key="MSI" data-label="MSI">{transaccion.msi}</td>;
                                    case "Banco": return <td key="Banco" data-label="Banco">{transaccion.bank}</td>;
                                    case "No. Autorizacion": return <td key="No. Autorizacion" data-label="No. Autorización">{transaccion.authorization_number}</td>;
                                    case "Comision": return <td key="Comision" data-label="Comisión">${parseFloat(transaccion.agreed_commission_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>;
                                    case "IVA Comision": return <td key="IVA Comision" data-label="IVA Comisión">${parseFloat(transaccion.iva_comm_val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>;
                                    case "Total": return <td key="Total" data-label="Total">${parseFloat(transaccion.trade_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>;
                                    case "Comisión": return <td key="Comisión" data-label="Comisión">${parseFloat(adjustedCommission).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>;
                                    case "Retención": return <td key="Retención" data-label="Retención">{transaccion.retention}%</td>;
                                    case "Estado": return <td key="Estado" data-label="Estado">{renderEstado(getEstadoEtiqueta(transaccion))}</td>;
                                    /*case "Acciones":
                                        const transactionDateTime = moment(transaccion.date);
                                        const nowInMexicoCity = moment.tz('America/Mexico_City');
                                        const elevenPmTodayInMexicoCity = nowInMexicoCity.clone().set({ hour: 23, minute: 0, second: 0, millisecond: 0 });
                                        const isSameDayInMexicoCity = transactionDateTime.isSame(nowInMexicoCity, 'day');
                                        const isBeforeElevenPmInMexicoCity = transactionDateTime.isSameOrBefore(elevenPmTodayInMexicoCity);

                                        const isActionEnabled = (userRole === 'ADMIN' || userRole === 'GERENTE') &&
                                                               transaccion.transaction_status === 'aprobada' &&
                                                               transaccion.transaction_type !== 'devolucion' &&
                                                               isSameDayInMexicoCity &&
                                                               isBeforeElevenPmInMexicoCity;

                                        return (
                                            <td key="Acciones" data-label="Acciones">
                                                {(userRole === 'SUPERADMIN' || userRole === 'ADMIN' || userRole === 'GERENTE') && transaccion.transaction_status === 'aprobada' && transaccion.transaction_type !== 'devolucion' && (
                                                    <Button
                                                        className='w-100 fw-bold col-sm-2'
                                                        type="button"
                                                        style={{
                                                            height: 'auto',
                                                            borderRadius: '8px',
                                                            border: isActionEnabled ? '1px solid #0E9600' : '1px solid #CCCCCC',
                                                            backgroundColor: isActionEnabled ? '#0E9600' : '#CCCCCC',
                                                            color: isActionEnabled ? '#FFFFFF' : '#9A9A9A',
                                                            cursor: isActionEnabled ? 'pointer' : 'not-allowed',
                                                            opacity: isActionEnabled ? 1 : 0.5,
                                                        }}
                                                        onClick={isActionEnabled ? () => handleShowModal(transaccion) : null}
                                                        disabled={!isActionEnabled}
                                                    >
                                                        <CDBIcon icon="undo-alt" />
                                                    </Button>
                                                )}
                                            </td>
                                        );
                                    default: return null; */
                                }
                            })}
                        </tr>
                    );
                })}
                {/* Filas de Totales */}
                <tr>
                    <td colSpan={allColumns.length - 1} style={{ textAlign: "right", fontWeight: "bold" }}>
                        Total ventas: ${totals.sales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                </tr>
                <tr>
                    <td colSpan={allColumns.length - 1} style={{ textAlign: "right", fontWeight: "bold" }}>
                        Total devoluciones: -${totals.returns.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                </tr>
                <tr>
                    <td colSpan={allColumns.length - 1} style={{ textAlign: "right", fontWeight: "bold" }}>
                        Total: ${totals.net.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                </tr>
            </CDBTableBody>
        </CDBTable>
    );
};

export default TransactionTable;
